import _ from "lodash";
import React, { useEffect } from "react";

// Components
import { FieldComponent } from "UnitPlans/components/IBcomponents";
import Loading from "UIComponents/Loading";

// DS
import { fontStyles } from "@toddle-edu/ds-theme";

// third party libs.
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";

// Utils
import { getFieldValue } from "Assessments/modules/Utils";
import {
  getTypeToRender,
  getFieldComponentProps,
  getIsDynamicField,
  updateLearningGoals,
  getSubFields,
} from "../../Utils";
import {
  INTERDISCIPLINARY_FIELD_MAPPING,
  ELEMENT_TYPE_ADD_LEARNING_GOALS,
} from "../../../../modules/Constants";

// Modules
import {
  updateAssignmentField,
  updateUnitPlanField,
} from "../../../../modules/LessonPlanningAssistantModule";

import { ELEMENT_TYPE_UNIT_PLAN, ASSESSMENT } from "Constants/stringConstants";
import {
  getCurrentCurriculumProgramType,
  getSelectedCurriculumId,
  getSelectedAcademicYear,
  getOrganizationId,
} from "modules/CommonSelectors";
import {
  getPlannerElementSetsOfCurriculumEnhancer,
  getPYPplannerElementNodeOfCurriculumEnhancer,
} from "modules/CommonQueryEnhancers";
import { getPlannerFieldOptions } from "UnitPlans/modules/UnitPlanModule";

const styles = {
  fieldComponentStyles: {
    labelStyle: {
      lineHeight: "2.4rem",
    },
    labelContainerStyle: {
      marginBottom: "0px",
    },
  },
  subTextStyle: {
    fontSize: "1.4rem",
    lineHeight: "2rem",
    marginBottom: "8px",
    ...fontStyles.medium,
  },
};

const FieldSelection = props => {
  const {
    t,
    type,
    field,
    fieldConfig,
    assessmentDetails,
    updateAssignmentField,
    portalType,
    fieldList,
    fieldValue,
    unitPlanFieldList,
    isUnitDependent,
    updateUnitPlanField,
    context,
    onClose,
    unitPlanFields,
    allFields,
    middlePaneRef,
    mode,
    curriculumType,
    sourceFrom,
    isFromUnitPlan,
    plannerElements,
    unitPlanId,
    courseId,
    isDynamicField,
    refetchAssessments,
    unitPlanTemplate,
    selectedAcademicYear,
    organizationId,
    curriculumId,
    course,
    fieldRef,
    updateParams,
    setUpdateParams,
    localContext,
    setLocalContext,
    onClickSave,
  } = props;

  useEffect(() => {
    setLocalContext(context?.[field] || "");
  }, [field]);

  // Set the initial value of the field in local state
  useEffect(() => {
    const _fieldList = isUnitDependent ? unitPlanFieldList : fieldList;

    // for field with subFields create local value like
    // value: {inquiryQuestion1: [], inquiryQuestion2: [], inquiryQuestion3: []}
    if (_.has(_fieldList[field], "subFields")) {
      const subFields = _.get(_fieldList[field], "subFields");

      const fieldValues = _.reduce(
        subFields,
        (acc, subField) => {
          const uid = `${field}_${subField.key}`;
          const fieldValue = getFieldValue({
            fields: allFields,
            uid,
          });

          if (!acc[subField.key]) {
            acc[subField.key] = [];
          }

          if (_.isArray(fieldValue)) {
            if (subField?.type === "RichText") {
              acc[subField.key] = !_.isEmpty(fieldValue) ? fieldValue : "";
            } else {
              acc[subField.key].push(...(fieldValue || []));
            }
          } else {
            acc[subField.key] = fieldValue;
          }
          return acc;
        },
        {}
      );

      setUpdateParams({
        [`${field}`]: fieldValues,
      });

      return;
    }

    // for fields those keys contain subject id or are dynamic fields
    // like disciplinaryObjectivesMYP_12345, disciplinaryObjectivesMYP_67890 etc
    if (isDynamicField) {
      const interdisciplinaryFieldUID = INTERDISCIPLINARY_FIELD_MAPPING[field];

      const fields = _.filter(unitPlanFields, ({ uid }) => {
        return _.startsWith(uid, interdisciplinaryFieldUID);
      });

      const values = _.compact(_.flattenDeep(_.map(fields, "value")));

      setUpdateParams({
        [`${field}`]: values,
      });
    } else {
      switch (field) {
        case "pedagogicalApproachDP": {
          if (_.every(fieldValue, item => _.has(item, "value"))) {
            const selectedValues = _.filter(fieldValue, item =>
              _.get(item, "isSelected", false)
            );

            setUpdateParams({
              [`${field}`]: _.map(selectedValues, "id"),
            });
          } else {
            setUpdateParams({
              [`${field}`]: fieldValue,
            });
          }

          break;
        }
        default: {
          switch (type) {
            case "RichText":
              setUpdateParams({
                [`${field}`]: !_.isEmpty(fieldValue) ? fieldValue : "",
              });
              break;
            default: {
              setUpdateParams({
                [`${field}`]: fieldValue,
              });
            }
          }
        }
      }
    }
  }, [field]);

  const value = _.get(updateParams, `${field}`);

  // all updateUnitPlan or updateAssessment based on the type of the field
  const updateInputField = params => {
    const commonParams = {
      t,
      ...updateParams,
      ...params,
      isDebounce: false,
      portalType,
    };

    if (isUnitDependent) {
      updateUnitPlanField({
        ...commonParams,
        unitPlanFields,
        field_list: unitPlanFieldList,
        unitPlanId,
      });
    } else {
      updateAssignmentField({
        ...commonParams,
        assessmentDetails,
        field_list: fieldList,
      });
    }
  };

  const renderBody = () => {
    if (_.isEmpty(updateParams)) return <Loading />;

    // Local fields specific to AI just to get user input for specific prompts (like location, geometric tools etc)
    if (fieldConfig?.isLocalField) {
      const {
        type,
        label,
        fieldTypeConfig,
        placeholder,
        options,
        ...restParams
      } = fieldConfig || {};

      const getPopupContainer = () => document.body;

      return (
        <FieldComponent
          type={type}
          value={localContext}
          updateInputField={e => setLocalContext(e?.params)}
          placeholder={placeholder ? placeholder : null}
          defaultOptions={options}
          fieldTypeConfig={fieldTypeConfig}
          getPopupContainer={getPopupContainer}
          {...restParams}
        />
      );
    }

    if (type) {
      const _field =
        type === ELEMENT_TYPE_ADD_LEARNING_GOALS ? "addLearningGoals" : field;

      const _fieldList = isUnitDependent
        ? { ...fieldList, ...unitPlanFieldList }
        : { ...unitPlanFieldList, ...fieldList };

      const {
        config: { subtext, fieldTypeConfig = {}, options = [] } = {},
      } = _fieldList[_field] ? _fieldList[_field] : {};

      const localisedOptions = middlePaneRef?.getOptionsLocale({
        t,
        key: _field,
        options,
      });

      let defaultOptions = middlePaneRef?.getOptions({
        fieldKey: _field,
        defaultOptions: localisedOptions,
        fieldObj: fieldList[_field],
        value,
        curriculumType,
      });

      if (isUnitDependent && type !== ELEMENT_TYPE_ADD_LEARNING_GOALS) {
        defaultOptions = getPlannerFieldOptions({
          t,
          plannerElements,
          unitPlanFields,
          fieldKey: _field,
          field_list: unitPlanFieldList,
          courseId,
          selectedAcademicYear,
          curriculumId,
          organizationId,
          grades: _.map(course?.grades, "id", []),
          curriculumType,
          objectiveLevelNodes: _.get(
            _.find(unitPlanFields, { uid: "objectiveLevel" }),
            "resolvedMinimalTree.nodes",
            []
          ),
        });
      }

      const assessmentType = getFieldValue({
        fields: assessmentDetails?.allFields,
        uid: "assessmentType",
      });

      const fieldComponentExtraProps = getFieldComponentProps({
        t,
        type,
        value,
        allFields,
        defaultOptions,
        assessmentDetails,
        field: props.field,
        fieldTypeConfig,
      });

      const fieldData = _.find(allFields, { uid: props.field });
      const fieldId = _.get(fieldData, "id", "");

      const subFields = getSubFields({
        t,
        unitPlanFields,
        fieldList: _fieldList,
        field: props.field,
        unitPlanTemplate,
      });

      const updateInputFieldLocal = (...args) => {
        const item = _.first(args);
        const { params, extraParams } = item || {};

        switch (_field) {
          case "unitPlan":
            middlePaneRef?.updateField(params);
            setUpdateParams(params);
            break;
          case "addLearningGoals": {
            updateLearningGoals({
              t,
              item,
              field: props.field,
              portalType,
              isUnitDependent,
              unitPlanFields,
              isDynamicField,
              updateUnitPlanField,
              unitPlanFieldList,
              unitPlanId,
              refetchAssessments,
              updateAssignmentField,
              assessmentDetails,
              fieldList,
              setUpdateParams,
              updateInputField,
            });
            break;
          }
          // directly update the field without having a local state
          case "relatedConcepts": {
            setUpdateParams(params);
            onClickSave({ params, extraParams, shouldCloseModal: false });
            break;
          }
          default: {
            setUpdateParams(params);
          }
        }
      };

      const setCustomRef = (key, ref) => {
        fieldRef.current[key] = ref;

        if (type === ELEMENT_TYPE_ADD_LEARNING_GOALS) {
          setTimeout(() => {
            ref?.onDropdownItemClick?.({
              key,
            });

            if (isUnitDependent) {
              ref?.learningGoalsModalRef?.current?.handleToggleSwitch?.();
            }
          }, 0);
        }
      };

      return (
        <FieldComponent
          t={t}
          portalType={portalType}
          assessmentMode={mode}
          assessmentType={assessmentType}
          unitPlanId={unitPlanId}
          parentId={isUnitDependent ? unitPlanId : assessmentDetails?.id}
          parentType={isUnitDependent ? ELEMENT_TYPE_UNIT_PLAN : ASSESSMENT}
          resolvedValue={_.get(fieldData, "resolvedMinimalTree", "")}
          fieldId={fieldId}
          subText={subtext}
          updateInputField={updateInputFieldLocal}
          unitPlanFields={unitPlanFields}
          unitPlanFieldList={unitPlanFieldList}
          defaultOptions={defaultOptions}
          onClose={onClose}
          sourceFrom={sourceFrom}
          isFromUnitPlan={isFromUnitPlan}
          styles={styles.fieldComponentStyles}
          subTextStyle={styles.subTextStyle}
          curriculumType={curriculumType}
          field_list={_fieldList}
          customRef={setCustomRef}
          selectedCourseId={courseId}
          fieldTypeConfig={fieldTypeConfig}
          skipQuery={!isUnitDependent}
          {..._.omit(_.get(_fieldList?.[props?.field], "config", {}), [
            ...(type === ELEMENT_TYPE_ADD_LEARNING_GOALS ? ["label"] : []),
          ])}
          {...fieldComponentExtraProps}
          subFields={subFields}
          shouldConsiderFieldValue={isUnitDependent}
          isLocalUpdate={true}
          debounceDuration={0}
        />
      );
    }

    return null;
  };

  return renderBody();
};

const mapStateToProps = (state, ownProps) => {
  const {
    field,
    fieldList,
    assessmentDetails,
    unitPlanDetails = {},
    unitPlanTemplate,
    isUnitDependent,
  } = ownProps;

  const { unitPlanId, unitPlanFields } = unitPlanDetails || {};

  const allFields = _.get(assessmentDetails, "allFields", []);

  const fieldValue = getFieldValue({
    fields: allFields,
    uid: field,
  });

  const unitPlanFieldValue = getFieldValue({
    fields: unitPlanFields,
    uid: field,
  });

  const selectedAcademicYear = getSelectedAcademicYear(state);
  const organizationId = getOrganizationId(state);
  const curriculumType = getCurrentCurriculumProgramType(state);
  const curriculumId = getSelectedCurriculumId(state);
  const curriculum = _.find(state.platform.organizationCurriculumPrograms, {
    type: curriculumType,
    id: curriculumId,
  });

  const unitPlanFieldList = _.get(unitPlanTemplate, "field_list", {});
  const currentFieldConfig = _.get(unitPlanFieldList, `${field}.config`, {});

  const currentFieldPlannerElementConfig = _.get(
    currentFieldConfig,
    `plannerElementConfig`,
    {}
  );

  const plannerElementTypes = _.get(currentFieldPlannerElementConfig, "type");

  const type = getTypeToRender({
    field,
    fieldList,
    unitPlanFieldList,
    isUnitDependent,
    unitPlanFields,
  });

  const isDynamicField = getIsDynamicField({
    field,
    isUnitDependent,
    unitPlanFields,
  });

  return {
    type,
    fieldValue: isUnitDependent ? unitPlanFieldValue : fieldValue,
    unitPlanFields,
    curriculumType,
    curriculumId,
    curriculumNodeInterfaceType: _.get(curriculum, "nodeInterfaceType", ""),
    allFields: isUnitDependent
      ? _.uniqBy([...unitPlanFields, ...allFields], "uid")
      : _.uniqBy([...allFields, ...unitPlanFields], "uid"),
    unitPlanId,
    unitPlanTemplate,
    plannerElementTypes,
    isStepWithPlannerElement: currentFieldConfig?.type === "PLANNER_ELEMENT",
    unitPlanFieldList,
    isDynamicField,
    organizationId,
    selectedAcademicYear,
    skipQuery: type === ELEMENT_TYPE_ADD_LEARNING_GOALS, // skip getPlannerElementSetsOfCurriculumEnhancer as it is internally called in AddLearningGoals
  };
};

const mapActionCreators = {
  updateAssignmentField,
  updateUnitPlanField,
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  getPlannerElementSetsOfCurriculumEnhancer,
  getPYPplannerElementNodeOfCurriculumEnhancer
)(FieldSelection);
